import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import {
  Observable,
  catchError,
  finalize,
  map,
  of,
  shareReplay,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { environment } from '@environments/environment';
import { CreateUserDto, UpdateUserDto, User } from 'core/models/user.model';
import { ALL_ROLES, Role } from 'core/models/role.model';
import { FamilySituation } from 'core/models/family-situation.model';
import { StudentEmergencyContact } from 'core/models/student-emergency-contact.model';
import { MOCK_USERS } from 'core/mocks/users.mock';
import {
  AcademicFormationType,
  BondStatus,
  EducationFunction,
  EmploymentType,
  FormationKnowledgeArea,
  FormationLevel,
  Gender,
  GuardianBond,
  HighSchoolInstitutionNature,
  HighSchoolType,
  HealthPlanCardFile,
  LocationZone,
  BloodType,
  NutritionalNeed,
  PersonDifferentiatedLocation,
  PersonProfile,
  QuilombolaFlag,
  RaceColor,
  Sex,
  StaffFormation,
  StaffProfile,
  SupportFunction,
} from 'core/models/cmdeb.model';
import { stripCepDigits } from 'shared/utils/cep.utils';
import { stripCpfDigits } from 'shared/utils/cpf.utils';
import { stripNisDigits } from 'shared/utils/nis.utils';
import { stripIbgeMunicipalityDigits } from 'shared/utils/ibge-municipality.utils';
import { stripInepCodeDigits } from 'shared/utils/inep-code.utils';

interface StaffFormationApiItem {
  formation_level?: string | null;
  course_name?: string | null;
  high_school_type?: string | null;
  high_school_institution_nature?: string | null;
  academic_formation_type?: string | null;
  academic_knowledge_area?: string | null;
  academic_institution_name?: string | null;
}

interface UsersApiResponse {
  ok: boolean;
  message: string;
  data: UsersApiItem[];
}

interface UserApiResponse {
  ok: boolean;
  message: string;
  data: UsersApiItem;
}

interface EmailAvailableApiResponse {
  ok: boolean;
  message: string;
  data: {
    available: boolean;
  };
}

interface UsersApiItem {
  id: number | string;
  name: string;
  email: string | null;
  role: string;
  sso_role?: string | null;
  institution_id?: number | null;
  institution_name?: string | null;
  registration_number?: string | null;
  person?: {
    id?: number | string;
    cpf?: string | null;
    rg_number?: string | null;
    rg_issuer?: string | null;
    rg_state?: string | null;
    rg_issued_on?: string | null;
    birth_city?: string | null;
    birth_state?: string | null;
    uses_school_transport?: string | null;
    birth_certificate_number?: string | null;
    birth_certificate_book?: string | null;
    birth_certificate_sheet?: string | null;
    birth_certificate_term?: string | null;
    birth_certificate_file?: HealthPlanCardFileApi | null;
    residence_proof_file?: HealthPlanCardFileApi | null;
    inep_code?: string | null;
    nis?: string | null;
    social_name?: string | null;
    phone?: string | null;
    birth_date?: string | null;
    sex?: string | null;
    gender?: string | null;
    race_color?: string | null;
    quilombola?: string | null;
    disabilities?: string[] | null;
    nutritional_need?: string | null;
    nutritional_need_details?: string | null;
    blood_type?: string | null;
    allergies?: string | null;
    medication?: string | null;
    medication_allergies?: string | null;
    has_private_health_plan?: boolean | null;
    health_plan_name?: string | null;
    health_plan_number?: string | null;
    health_plan_card_front?: HealthPlanCardFileApi | null;
    health_plan_card_back?: HealthPlanCardFileApi | null;
    mother_name?: string | null;
    father_name?: string | null;
    family_situation?: string | null;
    guardian_name?: string | null;
    guardian_relationship?: string | null;
    guardian_cpf?: string | null;
    guardian_phone?: string | null;
    guardian_email?: string | null;
    guardian2_name?: string | null;
    guardian2_relationship?: string | null;
    guardian2_cpf?: string | null;
    guardian2_phone?: string | null;
    guardian2_email?: string | null;
    nationality?: string | null;
    nationality_country_code?: string | null;
    postal_code?: string | null;
    street?: string | null;
    number?: string | null;
    address_complement?: string | null;
    district?: string | null;
    city?: string | null;
    state?: string | null;
    ibge_municipality_code?: string | null;
    location_zone?: string | null;
    differentiated_location?: string | null;
  } | null;
  staff?: {
    education_function?: string | null;
    support_function?: string | null;
    support_function_other?: string | null;
    formation_level?: string | null;
    course_name?: string | null;
    high_school_type?: string | null;
    high_school_institution_nature?: string | null;
    academic_formation_type?: string | null;
    academic_knowledge_area?: string | null;
    academic_institution_name?: string | null;
    formations?: StaffFormationApiItem[] | null;
    employment_type?: string | null;
    bond_status?: string | null;
    weekly_workload?: number | null;
  } | null;
  emergency_contacts?: EmergencyContactApiItem[] | null;
  active: boolean;
  has_system_access?: boolean;
  avatar_url?: string | null;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
}

interface EmergencyContactApiItem {
  id?: number | string;
  guardian_id?: number | string | null;
  guardian_name?: string | null;
  kinship?: string | null;
  sort_order?: number | null;
  name?: string | null;
  phone?: string | null;
  phone_is_whatsapp?: boolean | null;
  phone2?: string | null;
  phone2_is_whatsapp?: boolean | null;
}

interface HealthPlanCardFileApi {
  original_name?: string | null;
  mime?: string | null;
  size?: number | null;
}

@Injectable({ providedIn: 'root' })
export class UserService {
  private readonly http = inject(HttpClient);
  private readonly users = signal<User[]>(
    environment.useMockAuth ? [...MOCK_USERS] : [],
  );
  private readonly trashedUsers = signal<User[]>([]);
  private readonly mockTrashedIds = new Set<string>();
  private readonly mockActive = new Map<string, boolean>();

  readonly all = this.users.asReadonly();
  readonly trashed = this.trashedUsers.asReadonly();
  readonly loading = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly trashLoading = signal(false);
  readonly trashLoadError = signal<string | null>(null);

  private loadedKey: string | null = null;
  private inFlightKey: string | null = null;
  private inFlight$: Observable<User[]> | null = null;
  private loadGeneration = 0;

  load(options?: {
    role?: Role;
    educationFunction?: EducationFunction;
    force?: boolean;
  }): Observable<User[]> {
    const cacheKey = `${options?.role ?? '*'}:${options?.educationFunction ?? '*'}`;

    if (environment.useMockAuth) {
      const items = MOCK_USERS.filter((item) => {
        if (this.mockTrashedIds.has(item.id)) {
          return false;
        }
        if (options?.role && item.role !== options.role) {
          return false;
        }
        if (
          options?.educationFunction &&
          item.staff?.educationFunction !== options.educationFunction
        ) {
          return false;
        }
        return true;
      }).map((item) => this.withMockActive(item));
      this.users.set(items);
      this.loadError.set(null);
      this.loadedKey = cacheKey;
      return of(this.users());
    }

    if (!options?.force && this.loadedKey === cacheKey && !this.loadError()) {
      return of(this.users());
    }

    if (!options?.force && this.inFlight$ && this.inFlightKey === cacheKey) {
      return this.inFlight$;
    }

    this.loading.set(true);
    this.loadError.set(null);

    const params: Record<string, string> = {};
    if (options?.role) {
      params['role'] = options.role;
    }
    if (options?.educationFunction) {
      params['education_function'] = options.educationFunction;
    }

    const generation = ++this.loadGeneration;
    this.inFlightKey = cacheKey;
    this.inFlight$ = this.http
      .get<UsersApiResponse>(`${environment.apiUrl}/users`, { params })
      .pipe(
        map((response) =>
          (response.data ?? []).map((item) => this.mapApiUser(item)),
        ),
        tap((items) => {
          // Ignora resposta antiga se outro load mais recente ja comecou.
          if (generation !== this.loadGeneration) {
            return;
          }
          this.users.set(items);
          this.loadedKey = cacheKey;
          this.loading.set(false);
        }),
        catchError((error: unknown) => {
          if (generation === this.loadGeneration) {
            this.loading.set(false);
            this.loadError.set(this.resolveErrorMessage(error));
          }
          return of([]);
        }),
        finalize(() => {
          if (
            generation === this.loadGeneration &&
            this.inFlightKey === cacheKey
          ) {
            this.inFlight$ = null;
            this.inFlightKey = null;
          }
        }),
        shareReplay(1),
      );

    return this.inFlight$;
  }

  getById(id: string): User | undefined {
    return this.users().find((item) => item.id === id);
  }

  fetchById(
    id: string,
    options?: { force?: boolean },
  ): Observable<User | null> {
    if (environment.useMockAuth) {
      return of(this.getById(id) ?? null);
    }

    const cached = options?.force ? undefined : this.getById(id);
    if (cached) {
      return of(cached);
    }

    this.loading.set(true);

    return this.http
      .get<UserApiResponse>(`${environment.apiUrl}/users/${id}`)
      .pipe(
        map((response) => this.mapApiUser(response.data)),
        tap((user) => {
          this.users.update((items) => {
            if (items.some((item) => item.id === user.id)) {
              return items.map((item) => (item.id === user.id ? user : item));
            }
            return [user, ...items];
          });
          this.loading.set(false);
        }),
        catchError(() => {
          this.loading.set(false);
          return of(null);
        }),
      );
  }

  /**
   * Verifica se o e-mail já está cadastrado (inclui lixeira no backend).
   * @param exceptId public_id do usuário atual (edição), para ignorar o próprio e-mail.
   */
  checkEmailAvailable(email: string, exceptId?: string): Observable<boolean> {
    const normalized = email.trim().toLowerCase();
    if (normalized === '') {
      return of(true);
    }

    if (environment.useMockAuth) {
      return of(this.isEmailFreeInList(this.users(), normalized, exceptId));
    }

    const params: Record<string, string> = { email: normalized };
    if (exceptId) {
      params['except_id'] = exceptId;
    }

    return this.http
      .get<EmailAvailableApiResponse>(
        `${environment.apiUrl}/users/email-available`,
        { params },
      )
      .pipe(
        map((response) => response.data?.available !== false),
        catchError(() =>
          // API antiga (ex.: DEV sem o endpoint) - lista usuários e confere o e-mail.
          this.load().pipe(
            map((users) => this.isEmailFreeInList(users, normalized, exceptId)),
            catchError(() => of(true)),
          ),
        ),
      );
  }

  private isEmailFreeInList(
    users: User[],
    email: string,
    exceptId?: string,
  ): boolean {
    return !users.some(
      (item) =>
        (item.email ?? '').toLowerCase() === email &&
        (exceptId === undefined || item.id !== exceptId),
    );
  }

  getStudents(institutionId?: string): User[] {
    return this.users().filter((item) => {
      if (item.role !== 'STUDENT') {
        return false;
      }
      if (institutionId && item.institutionId !== institutionId) {
        return false;
      }
      return true;
    });
  }

  getTeachers(institutionId?: string): User[] {
    return this.users().filter((item) => {
      if (item.role !== 'TEACHER') {
        return false;
      }
      if (
        institutionId &&
        String(item.institutionId ?? '') !== String(institutionId)
      ) {
        return false;
      }
      return true;
    });
  }

  getGuardians(institutionId?: string): User[] {
    return this.users().filter((item) => {
      if (item.role !== 'GUARDIAN') {
        return false;
      }
      if (
        institutionId &&
        String(item.institutionId ?? '') !== String(institutionId)
      ) {
        return false;
      }
      return true;
    });
  }

  isSupportStaff(user: User): boolean {
    return user.staff?.educationFunction === 'support_staff';
  }

  getAllocationProfessors(institutionId?: string): User[] {
    return this.getTeachers(institutionId).filter(
      (item) => item.active && !this.isSupportStaff(item),
    );
  }

  getAllocationSupportStaff(institutionId?: string): User[] {
    return this.getTeachers(institutionId).filter(
      (item) => item.active && this.isSupportStaff(item),
    );
  }

  create(dto: CreateUserDto): Observable<User | null> {
    if (environment.useMockAuth) {
      const now = new Date().toISOString();
      const user: User = {
        id: crypto.randomUUID(),
        ...dto,
        email: dto.hasSystemAccess ? (dto.email ?? null) : null,
        hasSystemAccess: dto.hasSystemAccess,
        createdAt: now,
        updatedAt: now,
      };
      this.users.update((items) => [user, ...items]);
      return of(user);
    }

    const body: Record<string, unknown> = {
      name: dto.name,
      role: dto.role,
      active: dto.active,
      has_system_access: dto.hasSystemAccess,
    };

    if (dto.hasSystemAccess) {
      body['email'] = dto.email ?? null;
    } else {
      body['email'] = null;
    }

    if (dto.institutionId) {
      body['institution_id'] = dto.institutionId;
    }
    if (dto.registrationNumber !== undefined) {
      body['registration_number'] = dto.registrationNumber.trim() || null;
    }
    if (dto.person) {
      body['person'] = this.toPersonApi(dto.person);
    }
    if (dto.staff) {
      body['staff'] = this.toStaffApi(dto.staff);
    }
    if (dto.emergencyContacts !== undefined) {
      body['emergency_contacts'] = this.toEmergencyContactsApi(
        dto.emergencyContacts,
      );
    }

    return this.http
      .post<UserApiResponse>(`${environment.apiUrl}/users`, body)
      .pipe(
        map((response) => this.mapApiUser(response.data)),
        tap((user) => {
          this.users.update((items) => [user, ...items]);
        }),
        catchError((error: unknown) => {
          return throwError(() =>
            this.toFieldAwareError(error, 'Não foi possível criar o usuário.'),
          );
        }),
      );
  }

  update(id: string, dto: UpdateUserDto): Observable<User | null> {
    if (environment.useMockAuth) {
      let updated: User | undefined;
      this.users.update((items) =>
        items.map((item) => {
          if (item.id !== id) {
            return item;
          }
          updated = {
            ...item,
            ...dto,
            updatedAt: new Date().toISOString(),
          };
          return updated;
        }),
      );
      return of(updated ?? null);
    }

    const body: Record<string, unknown> = {};
    if (dto.name !== undefined) {
      body['name'] = dto.name;
    }
    if (dto.hasSystemAccess !== undefined) {
      body['has_system_access'] = dto.hasSystemAccess;
    }
    if (dto.email !== undefined && dto.hasSystemAccess !== false) {
      body['email'] = dto.email;
    }
    if (dto.role !== undefined) {
      body['role'] = dto.role;
    }
    if (dto.active !== undefined) {
      body['active'] = dto.active;
    }
    if (dto.institutionId !== undefined) {
      body['institution_id'] = dto.institutionId || null;
    }
    if (dto.registrationNumber !== undefined) {
      body['registration_number'] = dto.registrationNumber.trim() || null;
    }
    if (dto.person !== undefined) {
      body['person'] = this.toPersonApi(dto.person);
    }
    if (dto.staff !== undefined) {
      body['staff'] = this.toStaffApi(dto.staff);
    }
    if (dto.emergencyContacts !== undefined) {
      body['emergency_contacts'] = this.toEmergencyContactsApi(
        dto.emergencyContacts,
      );
    }

    return this.http
      .patch<UserApiResponse>(`${environment.apiUrl}/users/${id}`, body)
      .pipe(
        map((response) => this.mapApiUser(response.data)),
        tap((user) => {
          this.users.update((items) =>
            items.map((item) => (item.id === user.id ? user : item)),
          );
        }),
        catchError((error: unknown) => {
          return throwError(() =>
            this.toFieldAwareError(
              error,
              'Não foi possível atualizar o usuário.',
            ),
          );
        }),
      );
  }

  uploadReceivedDocument(
    userId: string,
    kind: 'birth_certificate' | 'residence_proof',
    file: File,
  ): Observable<User | null> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    return this.http
      .post(
        `${environment.apiUrl}/users/${userId}/received-documents/${kind}`,
        formData,
      )
      .pipe(
        switchMap(() => this.fetchById(userId, { force: true })),
        catchError((error: unknown) =>
          throwError(
            () =>
              new Error(
                this.resolveErrorMessage(
                  error,
                  'Não foi possível enviar o documento recebido.',
                ),
              ),
          ),
        ),
      );
  }

  deleteReceivedDocument(
    userId: string,
    kind: 'birth_certificate' | 'residence_proof',
  ): Observable<User | null> {
    return this.http
      .delete(
        `${environment.apiUrl}/users/${userId}/received-documents/${kind}`,
      )
      .pipe(
        switchMap(() => this.fetchById(userId, { force: true })),
        catchError((error: unknown) =>
          throwError(
            () =>
              new Error(
                this.resolveErrorMessage(
                  error,
                  'Não foi possível remover o documento recebido.',
                ),
              ),
          ),
        ),
      );
  }

  downloadReceivedDocument(
    userId: string,
    kind: 'birth_certificate' | 'residence_proof',
  ): Observable<Blob> {
    return this.http
      .get(`${environment.apiUrl}/users/${userId}/received-documents/${kind}`, {
        responseType: 'blob',
      })
      .pipe(
        catchError((error: unknown) =>
          throwError(
            () =>
              new Error(
                this.resolveErrorMessage(
                  error,
                  'Não foi possível baixar o documento recebido.',
                ),
              ),
          ),
        ),
      );
  }

  uploadHealthPlanCard(
    userId: string,
    side: 'front' | 'back',
    file: File,
  ): Observable<User | null> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    return this.http
      .post(
        `${environment.apiUrl}/users/${userId}/health-plan-card/${side}`,
        formData,
      )
      .pipe(
        switchMap(() => this.fetchById(userId, { force: true })),
        catchError((error: unknown) =>
          throwError(
            () =>
              new Error(
                this.resolveErrorMessage(
                  error,
                  'Não foi possível enviar a carteira do plano de saúde.',
                ),
              ),
          ),
        ),
      );
  }

  uploadAvatar(userId: string, file: File): Observable<User | null> {
    if (environment.useMockAuth) {
      return new Observable<User | null>((subscriber) => {
        const reader = new FileReader();
        reader.onload = () => {
          const avatarUrl =
            typeof reader.result === 'string' ? reader.result : null;
          let updated: User | null = null;
          this.users.update((items) =>
            items.map((item) => {
              if (item.id !== userId) {
                return item;
              }
              updated = {
                ...item,
                avatarUrl,
                updatedAt: new Date().toISOString(),
              };
              return updated;
            }),
          );
          subscriber.next(updated);
          subscriber.complete();
        };
        reader.onerror = () => {
          subscriber.error(new Error('Não foi possível ler a foto.'));
        };
        reader.readAsDataURL(file);
      });
    }

    const formData = new FormData();
    formData.append('file', file, file.name);

    return this.http
      .post<UserApiResponse>(
        `${environment.apiUrl}/users/${userId}/avatar`,
        formData,
      )
      .pipe(
        map((response) => this.mapApiUser(response.data)),
        tap((user) => {
          this.users.update((items) => {
            const exists = items.some((item) => item.id === user.id);
            if (!exists) {
              return [...items, user];
            }
            return items.map((item) => (item.id === user.id ? user : item));
          });
        }),
        catchError((error: unknown) =>
          throwError(
            () =>
              new Error(
                this.resolveErrorMessage(
                  error,
                  'Não foi possível enviar a foto do perfil.',
                ),
              ),
          ),
        ),
      );
  }

  deleteAvatar(userId: string): Observable<User | null> {
    if (environment.useMockAuth) {
      let updated: User | null = null;
      this.users.update((items) =>
        items.map((item) => {
          if (item.id !== userId) {
            return item;
          }
          updated = {
            ...item,
            avatarUrl: null,
            updatedAt: new Date().toISOString(),
          };
          return updated;
        }),
      );
      return of(updated);
    }

    return this.http
      .delete<UserApiResponse>(`${environment.apiUrl}/users/${userId}/avatar`)
      .pipe(
        map((response) => this.mapApiUser(response.data)),
        tap((user) => {
          this.users.update((items) =>
            items.map((item) => (item.id === user.id ? user : item)),
          );
        }),
        catchError((error: unknown) =>
          throwError(
            () =>
              new Error(
                this.resolveErrorMessage(
                  error,
                  'Não foi possível remover a foto do perfil.',
                ),
              ),
          ),
        ),
      );
  }

  deleteHealthPlanCard(
    userId: string,
    side: 'front' | 'back',
  ): Observable<User | null> {
    return this.http
      .delete(`${environment.apiUrl}/users/${userId}/health-plan-card/${side}`)
      .pipe(
        switchMap(() => this.fetchById(userId, { force: true })),
        catchError((error: unknown) =>
          throwError(
            () =>
              new Error(
                this.resolveErrorMessage(
                  error,
                  'Não foi possível remover a carteira do plano de saúde.',
                ),
              ),
          ),
        ),
      );
  }

  downloadHealthPlanCard(
    userId: string,
    side: 'front' | 'back',
  ): Observable<Blob> {
    return this.http
      .get(`${environment.apiUrl}/users/${userId}/health-plan-card/${side}`, {
        responseType: 'blob',
      })
      .pipe(
        catchError((error: unknown) =>
          throwError(
            () =>
              new Error(
                this.resolveErrorMessage(
                  error,
                  'Não foi possível baixar a carteira do plano de saúde.',
                ),
              ),
          ),
        ),
      );
  }

  setActive(id: string, active: boolean): Observable<User | null> {
    if (environment.useMockAuth) {
      this.mockActive.set(id, active);
      let updated: User | undefined;
      this.users.update((items) =>
        items.map((item) => {
          if (item.id !== id) {
            return item;
          }
          updated = {
            ...item,
            active,
            updatedAt: new Date().toISOString(),
          };
          return updated;
        }),
      );
      return of(updated ?? null);
    }

    const action = active ? 'activate' : 'deactivate';
    return this.http
      .patch<UserApiResponse>(`${environment.apiUrl}/users/${id}/${action}`, {})
      .pipe(
        map((response) => this.mapApiUser(response.data)),
        tap((user) => {
          this.users.update((items) =>
            items.map((item) => (item.id === user.id ? user : item)),
          );
        }),
        catchError(() => of(null)),
      );
  }

  delete(id: string): Observable<boolean> {
    if (environment.useMockAuth) {
      const current = this.users().find((item) => item.id === id);
      if (!current) {
        return of(false);
      }
      this.mockTrashedIds.add(id);
      const trashed: User = {
        ...current,
        active: false,
        deletedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      this.users.update((items) => items.filter((item) => item.id !== id));
      this.trashedUsers.update((items) => [trashed, ...items]);
      this.loadedKey = null;
      return of(true);
    }

    return this.http
      .delete<UserApiResponse>(`${environment.apiUrl}/users/${id}`)
      .pipe(
        tap(() => {
          this.users.update((items) => items.filter((item) => item.id !== id));
          this.loadedKey = null;
        }),
        map(() => true),
        catchError(() => of(false)),
      );
  }

  loadTrashed(options?: {
    role?: Role;
    educationFunction?: EducationFunction;
  }): Observable<User[]> {
    if (environment.useMockAuth) {
      const items = this.trashedUsers().filter((item) => {
        if (options?.role && item.role !== options.role) {
          return false;
        }
        if (
          options?.educationFunction &&
          item.staff?.educationFunction !== options.educationFunction
        ) {
          return false;
        }
        return true;
      });
      this.trashLoadError.set(null);
      return of(items);
    }

    this.trashLoading.set(true);
    this.trashLoadError.set(null);

    const params: Record<string, string> = {};
    if (options?.role) {
      params['role'] = options.role;
    }
    if (options?.educationFunction) {
      params['education_function'] = options.educationFunction;
    }

    return this.http
      .get<UsersApiResponse>(`${environment.apiUrl}/users/trashed`, { params })
      .pipe(
        map((response) =>
          (response.data ?? []).map((item) => this.mapApiUser(item)),
        ),
        tap((items) => {
          this.trashedUsers.set(items);
          this.trashLoading.set(false);
        }),
        catchError((error: unknown) => {
          this.trashLoading.set(false);
          this.trashLoadError.set(this.resolveErrorMessage(error));
          return of([]);
        }),
      );
  }

  restore(id: string): Observable<User | null> {
    if (environment.useMockAuth) {
      const current = this.trashedUsers().find((item) => item.id === id);
      if (!current) {
        return of(null);
      }
      this.mockTrashedIds.delete(id);
      const restored: User = {
        ...current,
        deletedAt: undefined,
        updatedAt: new Date().toISOString(),
      };
      this.trashedUsers.update((items) =>
        items.filter((item) => item.id !== id),
      );
      this.users.update((items) =>
        items.some((item) => item.id === id) ? items : [restored, ...items],
      );
      this.loadedKey = null;
      return of(restored);
    }

    return this.http
      .post<UserApiResponse>(`${environment.apiUrl}/users/${id}/restore`, {})
      .pipe(
        map((response) => this.mapApiUser(response.data)),
        tap((user) => {
          this.trashedUsers.update((items) =>
            items.filter((item) => item.id !== user.id),
          );
          this.users.update((items) =>
            items.some((item) => item.id === user.id)
              ? items.map((item) => (item.id === user.id ? user : item))
              : [user, ...items],
          );
          this.loadedKey = null;
        }),
        catchError(() => of(null)),
      );
  }

  forceDelete(id: string): Observable<boolean> {
    if (environment.useMockAuth) {
      const exists = this.trashedUsers().some((item) => item.id === id);
      if (!exists) {
        return of(false);
      }
      this.mockTrashedIds.delete(id);
      this.trashedUsers.update((items) =>
        items.filter((item) => item.id !== id),
      );
      return of(true);
    }

    return this.http.delete(`${environment.apiUrl}/users/${id}/force`).pipe(
      tap(() => {
        this.trashedUsers.update((items) =>
          items.filter((item) => item.id !== id),
        );
      }),
      map(() => true),
      catchError(() => of(false)),
    );
  }

  private withMockActive(item: User): User {
    const active = this.mockActive.get(item.id);
    return active === undefined ? item : { ...item, active };
  }

  private mapApiUser(item: UsersApiItem): User {
    return {
      id: String(item.id),
      name: item.name,
      email:
        typeof item.email === 'string' && item.email.trim() !== ''
          ? item.email.trim()
          : null,
      role: this.normalizeRole(item.role),
      institutionId:
        item.institution_id !== null && item.institution_id !== undefined
          ? String(item.institution_id)
          : undefined,
      institutionName:
        typeof item.institution_name === 'string' &&
        item.institution_name.trim() !== ''
          ? item.institution_name.trim()
          : undefined,
      registrationNumber:
        typeof item.registration_number === 'string' &&
        item.registration_number.trim() !== ''
          ? item.registration_number.trim()
          : undefined,
      active: item.active,
      hasSystemAccess: item.has_system_access !== false,
      avatarUrl:
        typeof item.avatar_url === 'string' && item.avatar_url.trim() !== ''
          ? item.avatar_url.trim()
          : null,
      person: item.person
        ? {
            id:
              item.person.id !== undefined ? String(item.person.id) : undefined,
            cpf: item.person.cpf ?? undefined,
            rgNumber: item.person.rg_number ?? undefined,
            rgIssuer: item.person.rg_issuer ?? undefined,
            rgState: item.person.rg_state ?? undefined,
            rgIssuedOn: item.person.rg_issued_on ?? undefined,
            birthCity: item.person.birth_city ?? undefined,
            birthState: item.person.birth_state ?? undefined,
            usesSchoolTransport:
              item.person.uses_school_transport === 'yes' ||
              item.person.uses_school_transport === 'no'
                ? item.person.uses_school_transport
                : undefined,
            birthCertificateNumber:
              item.person.birth_certificate_number ?? undefined,
            birthCertificateBook:
              item.person.birth_certificate_book ?? undefined,
            birthCertificateSheet:
              item.person.birth_certificate_sheet ?? undefined,
            birthCertificateTerm:
              item.person.birth_certificate_term ?? undefined,
            birthCertificateFile: this.mapHealthPlanCardFile(
              item.person.birth_certificate_file,
            ),
            residenceProofFile: this.mapHealthPlanCardFile(
              item.person.residence_proof_file,
            ),
            inepCode: item.person.inep_code ?? undefined,
            nis: item.person.nis ?? undefined,
            socialName: item.person.social_name ?? undefined,
            phone: item.person.phone ?? undefined,
            birthDate: item.person.birth_date ?? undefined,
            sex: (item.person.sex ?? undefined) as Sex | undefined,
            gender: (item.person.gender ?? undefined) as Gender | undefined,
            raceColor: (item.person.race_color ?? undefined) as
              | RaceColor
              | undefined,
            quilombola: (item.person.quilombola ?? undefined) as
              | QuilombolaFlag
              | undefined,
            disabilities: Array.isArray(item.person.disabilities)
              ? item.person.disabilities.map((item) => String(item))
              : undefined,
            nutritionalNeed: (item.person.nutritional_need ?? undefined) as
              | NutritionalNeed
              | undefined,
            nutritionalNeedDetails:
              item.person.nutritional_need_details ?? undefined,
            bloodType: (item.person.blood_type ?? undefined) as
              | BloodType
              | undefined,
            allergies: item.person.allergies ?? undefined,
            medication: item.person.medication ?? undefined,
            medicationAllergies: item.person.medication_allergies ?? undefined,
            hasPrivateHealthPlan: this.coerceOptionalBoolean(
              item.person.has_private_health_plan,
            ),
            healthPlanName: item.person.health_plan_name ?? undefined,
            healthPlanNumber: item.person.health_plan_number ?? undefined,
            healthPlanCardFront: this.mapHealthPlanCardFile(
              item.person.health_plan_card_front,
            ),
            healthPlanCardBack: this.mapHealthPlanCardFile(
              item.person.health_plan_card_back,
            ),
            motherName: item.person.mother_name ?? undefined,
            fatherName: item.person.father_name ?? undefined,
            familySituation: (item.person.family_situation ?? undefined) as
              | FamilySituation
              | undefined,
            guardianName: item.person.guardian_name ?? undefined,
            guardianRelationship: (item.person.guardian_relationship ??
              undefined) as GuardianBond | undefined,
            guardianCpf: item.person.guardian_cpf ?? undefined,
            guardianPhone: item.person.guardian_phone ?? undefined,
            guardianEmail: item.person.guardian_email ?? undefined,
            guardian2Name: item.person.guardian2_name ?? undefined,
            guardian2Relationship: (item.person.guardian2_relationship ??
              undefined) as GuardianBond | undefined,
            guardian2Cpf: item.person.guardian2_cpf ?? undefined,
            guardian2Phone: item.person.guardian2_phone ?? undefined,
            guardian2Email: item.person.guardian2_email ?? undefined,
            nationality: item.person.nationality ?? undefined,
            nationalityCountryCode:
              item.person.nationality_country_code ?? undefined,
            postalCode: item.person.postal_code ?? undefined,
            street: item.person.street ?? undefined,
            number: item.person.number ?? undefined,
            addressComplement: item.person.address_complement ?? undefined,
            district: item.person.district ?? undefined,
            city: item.person.city ?? undefined,
            state: item.person.state ?? undefined,
            ibgeMunicipalityCode:
              item.person.ibge_municipality_code ?? undefined,
            locationZone: (item.person.location_zone ?? undefined) as
              | LocationZone
              | undefined,
            differentiatedLocation: (item.person.differentiated_location ??
              undefined) as PersonDifferentiatedLocation | undefined,
          }
        : undefined,
      staff: item.staff
        ? {
            educationFunction: (item.staff.education_function ?? undefined) as
              | EducationFunction
              | undefined,
            supportFunction: (item.staff.support_function ?? undefined) as
              | SupportFunction
              | undefined,
            supportFunctionOther:
              item.staff.support_function_other ?? undefined,
            formations: Array.isArray(item.staff.formations)
              ? item.staff.formations.map((row) => this.mapStaffFormation(row))
              : undefined,
            formationLevel: (item.staff.formation_level ?? undefined) as
              | FormationLevel
              | undefined,
            courseName: item.staff.course_name ?? undefined,
            highSchoolType: (item.staff.high_school_type ?? undefined) as
              | HighSchoolType
              | undefined,
            highSchoolInstitutionNature: (item.staff
              .high_school_institution_nature ?? undefined) as
              | HighSchoolInstitutionNature
              | undefined,
            academicFormationType: (item.staff.academic_formation_type ??
              undefined) as AcademicFormationType | undefined,
            academicKnowledgeArea: (item.staff.academic_knowledge_area ??
              undefined) as FormationKnowledgeArea | undefined,
            academicInstitutionName:
              item.staff.academic_institution_name ?? undefined,
            employmentType: (item.staff.employment_type ?? undefined) as
              | EmploymentType
              | undefined,
            bondStatus: (item.staff.bond_status ?? undefined) as
              | BondStatus
              | undefined,
            weeklyWorkload:
              typeof item.staff.weekly_workload === 'number'
                ? item.staff.weekly_workload
                : undefined,
          }
        : undefined,
      emergencyContacts: Array.isArray(item.emergency_contacts)
        ? item.emergency_contacts
            .map((row) => this.mapEmergencyContact(row))
            .filter((row): row is StudentEmergencyContact => row !== null)
        : undefined,
      createdAt: item.created_at ?? new Date().toISOString(),
      updatedAt: item.updated_at ?? new Date().toISOString(),
      deletedAt: item.deleted_at ?? undefined,
    };
  }

  private mapEmergencyContact(
    item: EmergencyContactApiItem,
  ): StudentEmergencyContact | null {
    const name = typeof item.name === 'string' ? item.name.trim() : '';
    if (!name) {
      return null;
    }

    return {
      id:
        item.id !== undefined && item.id !== null ? String(item.id) : undefined,
      guardianId:
        item.guardian_id !== undefined && item.guardian_id !== null
          ? String(item.guardian_id)
          : undefined,
      guardianName: item.guardian_name ?? undefined,
      kinship:
        typeof item.kinship === 'string' && item.kinship.trim()
          ? (item.kinship.trim() as StudentEmergencyContact['kinship'])
          : undefined,
      sortOrder:
        typeof item.sort_order === 'number' ? item.sort_order : undefined,
      name,
      phone: item.phone ?? undefined,
      phoneIsWhatsapp: Boolean(item.phone_is_whatsapp),
      phone2: item.phone2 ?? undefined,
      phone2IsWhatsapp: Boolean(item.phone2_is_whatsapp),
    };
  }

  private coerceOptionalBoolean(value: unknown): boolean | undefined {
    if (value === true || value === 1 || value === '1' || value === 'true') {
      return true;
    }
    if (value === false || value === 0 || value === '0' || value === 'false') {
      return false;
    }
    return undefined;
  }

  private mapHealthPlanCardFile(
    item: HealthPlanCardFileApi | null | undefined,
  ): HealthPlanCardFile | undefined {
    if (!item || typeof item !== 'object') {
      return undefined;
    }
    const originalName =
      typeof item.original_name === 'string' ? item.original_name.trim() : '';
    if (!originalName) {
      return undefined;
    }
    return {
      originalName,
      mime:
        typeof item.mime === 'string' && item.mime.trim() !== ''
          ? item.mime
          : 'application/octet-stream',
      size: typeof item.size === 'number' ? item.size : undefined,
    };
  }

  private mapStaffFormation(item: StaffFormationApiItem): StaffFormation {
    return {
      formationLevel: (item.formation_level ?? undefined) as
        | FormationLevel
        | undefined,
      courseName: item.course_name ?? undefined,
      highSchoolType: (item.high_school_type ?? undefined) as
        | HighSchoolType
        | undefined,
      highSchoolInstitutionNature: (item.high_school_institution_nature ??
        undefined) as HighSchoolInstitutionNature | undefined,
      academicFormationType: (item.academic_formation_type ?? undefined) as
        | AcademicFormationType
        | undefined,
      academicKnowledgeArea: (item.academic_knowledge_area ?? undefined) as
        | FormationKnowledgeArea
        | undefined,
      academicInstitutionName: item.academic_institution_name ?? undefined,
    };
  }

  private toPersonApi(person: PersonProfile): Record<string, unknown> {
    return {
      cpf: person.cpf ? stripCpfDigits(person.cpf) || null : null,
      rg_number: person.rgNumber?.trim() || null,
      rg_issuer: person.rgIssuer?.trim() || null,
      rg_state: person.rgState?.trim().toUpperCase() || null,
      rg_issued_on: person.rgIssuedOn?.trim() || null,
      birth_city: person.birthCity?.trim() || null,
      birth_state: person.birthState?.trim().toUpperCase() || null,
      uses_school_transport: person.usesSchoolTransport || null,
      birth_certificate_number: person.birthCertificateNumber
        ? person.birthCertificateNumber.replace(/\D/g, '') || null
        : null,
      birth_certificate_book: person.birthCertificateBook?.trim() || null,
      birth_certificate_sheet: person.birthCertificateSheet?.trim() || null,
      birth_certificate_term: person.birthCertificateTerm?.trim() || null,
      inep_code: person.inepCode
        ? stripInepCodeDigits(person.inepCode, 12) || null
        : null,
      nis: person.nis ? stripNisDigits(person.nis) || null : null,
      social_name: person.socialName ?? null,
      phone: person.phone ?? null,
      birth_date: person.birthDate ?? null,
      sex: person.sex ?? null,
      gender: person.gender ?? null,
      race_color: person.raceColor ?? null,
      quilombola: person.quilombola ?? null,
      disabilities: person.disabilities ?? null,
      nutritional_need: person.nutritionalNeed ?? null,
      nutritional_need_details:
        person.nutritionalNeed === 'yes'
          ? (person.nutritionalNeedDetails ?? null)
          : null,
      blood_type: person.bloodType ?? null,
      allergies: person.allergies ?? null,
      medication: person.medication ?? null,
      medication_allergies: person.medicationAllergies ?? null,
      has_private_health_plan:
        person.hasPrivateHealthPlan === true
          ? true
          : person.hasPrivateHealthPlan === false
            ? false
            : null,
      health_plan_name: person.healthPlanName ?? null,
      health_plan_number: person.healthPlanNumber ?? null,
      mother_name: person.motherName ?? null,
      father_name: person.fatherName ?? null,
      family_situation: person.familySituation ?? null,
      guardian_name: person.guardianName ?? null,
      guardian_relationship: person.guardianRelationship ?? null,
      guardian_cpf: person.guardianCpf
        ? stripCpfDigits(person.guardianCpf) || null
        : null,
      guardian_phone: person.guardianPhone ?? null,
      guardian_email: person.guardianEmail ?? null,
      guardian2_name: person.guardian2Name ?? null,
      guardian2_relationship: person.guardian2Relationship ?? null,
      guardian2_cpf: person.guardian2Cpf
        ? stripCpfDigits(person.guardian2Cpf) || null
        : null,
      guardian2_phone: person.guardian2Phone ?? null,
      guardian2_email: person.guardian2Email ?? null,
      nationality: person.nationality ?? null,
      nationality_country_code: person.nationalityCountryCode
        ? String(person.nationalityCountryCode)
            .replace(/\D/g, '')
            .slice(0, 3) || null
        : null,
      postal_code: person.postalCode
        ? stripCepDigits(person.postalCode) || null
        : null,
      street: person.street ?? null,
      number: person.number ?? null,
      address_complement: person.addressComplement ?? null,
      district: person.district ?? null,
      city: person.city ?? null,
      state: person.state ?? null,
      ibge_municipality_code: person.ibgeMunicipalityCode
        ? stripIbgeMunicipalityDigits(person.ibgeMunicipalityCode) || null
        : null,
      location_zone: person.locationZone ?? null,
      differentiated_location: person.differentiatedLocation ?? null,
    };
  }

  private toEmergencyContactsApi(
    contacts: readonly StudentEmergencyContact[],
  ): Record<string, unknown>[] {
    const items: Record<string, unknown>[] = [];
    for (const item of contacts) {
      const name = item.name.trim();
      if (!name) {
        continue;
      }
      items.push({
        guardian_id: item.guardianId?.trim() || null,
        kinship: item.kinship?.trim() || null,
        name,
        phone: item.phone?.trim() || null,
        phone_is_whatsapp: Boolean(item.phoneIsWhatsapp),
        phone2: item.phone2?.trim() || null,
        phone2_is_whatsapp: Boolean(item.phone2IsWhatsapp),
      });
    }
    return items;
  }

  private toStaffApi(staff: StaffProfile): Record<string, unknown> {
    return {
      education_function: staff.educationFunction ?? null,
      support_function: staff.supportFunction ?? null,
      support_function_other:
        staff.supportFunction === 'other'
          ? (staff.supportFunctionOther ?? null)
          : null,
      formations: Array.isArray(staff.formations)
        ? staff.formations.map((item) => this.toStaffFormationApi(item))
        : undefined,
      formation_level: staff.formationLevel ?? null,
      course_name: staff.courseName ?? null,
      high_school_type: staff.highSchoolType ?? null,
      high_school_institution_nature: staff.highSchoolInstitutionNature ?? null,
      academic_formation_type: staff.academicFormationType ?? null,
      academic_knowledge_area: staff.academicKnowledgeArea ?? null,
      academic_institution_name: staff.academicInstitutionName ?? null,
      employment_type: staff.employmentType ?? null,
      bond_status: staff.bondStatus ?? null,
      weekly_workload:
        typeof staff.weeklyWorkload === 'number' ? staff.weeklyWorkload : null,
    };
  }

  private toStaffFormationApi(item: StaffFormation): StaffFormationApiItem {
    return {
      formation_level: item.formationLevel ?? null,
      course_name: item.courseName ?? null,
      high_school_type: item.highSchoolType ?? null,
      high_school_institution_nature: item.highSchoolInstitutionNature ?? null,
      academic_formation_type: item.academicFormationType ?? null,
      academic_knowledge_area: item.academicKnowledgeArea ?? null,
      academic_institution_name: item.academicInstitutionName ?? null,
    };
  }

  private normalizeRole(value: string): Role {
    if ((ALL_ROLES as readonly string[]).includes(value)) {
      return value as Role;
    }
    return 'STUDENT';
  }

  private resolveErrorMessage(
    error: unknown,
    fallback = 'Não foi possível carregar os usuários. Tente novamente.',
  ): string {
    const body = this.apiErrorBody(error);
    if (body === null) {
      return fallback;
    }

    const field = this.firstFieldKey(body.errors);
    const fieldError = this.firstFieldError(body.errors);
    if (fieldError) {
      return this.translateKnownError(fieldError, field);
    }

    if (typeof body.message === 'string' && body.message.trim() !== '') {
      return this.translateKnownError(body.message, field);
    }

    return fallback;
  }

  private toFieldAwareError(error: unknown, fallback: string): Error {
    const field = this.firstFieldKey(this.apiErrorBody(error)?.errors);
    const message = this.resolveErrorMessage(error, fallback);
    return Object.assign(new Error(message), { field });
  }

  private apiErrorBody(error: unknown): {
    message?: string;
    errors?: Record<string, unknown>;
  } | null {
    if (typeof error !== 'object' || error === null || !('error' in error)) {
      return null;
    }

    const body = (error as { error?: unknown }).error;
    if (typeof body !== 'object' || body === null) {
      return null;
    }

    return body as { message?: string; errors?: Record<string, unknown> };
  }

  private firstFieldKey(
    errors: Record<string, unknown> | undefined,
  ): string | null {
    if (!errors) {
      return null;
    }

    for (const [key, messages] of Object.entries(errors)) {
      if (typeof messages === 'string' && messages.trim() !== '') {
        return key;
      }
      if (Array.isArray(messages)) {
        const first = messages.find(
          (item): item is string =>
            typeof item === 'string' && item.trim() !== '',
        );
        if (first) {
          return key;
        }
      }
    }

    return null;
  }

  private firstFieldError(
    errors: Record<string, unknown> | undefined,
  ): string | null {
    if (!errors) {
      return null;
    }

    for (const messages of Object.values(errors)) {
      if (typeof messages === 'string' && messages.trim() !== '') {
        return messages;
      }
      if (Array.isArray(messages)) {
        const first = messages.find(
          (item): item is string =>
            typeof item === 'string' && item.trim() !== '',
        );
        if (first) {
          return first;
        }
      }
    }

    return null;
  }

  private translateKnownError(message: string, field?: string | null): string {
    const normalized = message.trim().toLowerCase();
    if (
      normalized === 'the email has already been taken.' ||
      normalized === 'the email has already been taken' ||
      normalized === 'this email is already in use.' ||
      normalized === 'this email is already in use'
    ) {
      return 'Este e-mail já está em uso.';
    }

    if (
      normalized === 'this cpf is already registered for another person.' ||
      normalized === 'this cpf is already registered for another person' ||
      normalized.includes('cpf already') ||
      normalized.includes('people_cpf_unique')
    ) {
      return 'Este CPF já está cadastrado para outra pessoa.';
    }

    if (
      normalized === 'validation.digits' ||
      normalized.startsWith('validation.digits')
    ) {
      const key = (field ?? '').toLowerCase();
      if (key.includes('ibge_municipality')) {
        return 'O código IBGE do município deve ter exatamente 7 dígitos.';
      }
      if (key.includes('nis')) {
        return 'O NIS deve ter exatamente 11 dígitos.';
      }
      if (key.includes('nationality_country')) {
        return 'O código do país (nacionalidade) deve ter exatamente 3 dígitos.';
      }
      if (key.includes('inep')) {
        return 'O código INEP deve ter entre 1 e 12 dígitos.';
      }
      return 'Informe a quantidade correta de dígitos numéricos no campo indicado.';
    }

    if (
      normalized === 'validation.digits_between' ||
      normalized.startsWith('validation.digits_between')
    ) {
      const key = (field ?? '').toLowerCase();
      if (key.includes('inep')) {
        return 'O código INEP deve ter entre 1 e 12 dígitos.';
      }
      return 'Informe a quantidade correta de dígitos numéricos no campo indicado.';
    }

    return message;
  }
}
